import { PureComponent } from 'react';

import qs from 'qs';
import { connect } from 'react-redux';

import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';

import { Repository } from 'src/components/types';
import { SOURCE_FILE_LOAD, MetricSender } from 'src/metrics';
import {
  initiatePendingDelete,
  initiatePendingEdit,
  initiatePendingRename,
} from 'src/redux/pending-commit';
import { RouteComponentProps } from 'src/router/types';
import { withRouter } from 'src/router/utils';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';
import { Dispatch } from 'src/types/state';
import urls from 'src/urls/source';
import { publishUiEvent } from 'src/utils/analytics/publish';

import CommitModalDialog from '../../../containers/commit-modal-dialog';
import { Ref, RepositoryPrivilege } from '../../../types';
import { UNLOAD_FILE } from '../actions';
import loadSourceFile from '../actions/load-source-file';
import File, { FileEditorModeChangeEvent } from '../components/file';
import { FileEditorMode, FileMeta } from '../types';

type ConnectedSourceFileProps = {
  hash: string;
  initialFileEditorMode: FileEditorMode;
  isMonacoEditorEnabled?: boolean;
  path: string;
  repository: Repository;
};

// @ts-ignore TODO: fix noImplicitAny error here
const mapStateToProps = (state, ownProps) => {
  const { file, meta, isLoading } = state.repository.source.file;
  const { atRef, ref } = state.repository.source.section;
  const queryParams = qs.parse(ownProps.location.search, {
    ignoreQueryPrefix: true,
  });

  return {
    atRef,
    contents: file ? file.contents : undefined,
    currentRef: ref,
    initialFileEditorMode:
      ownProps.isMonacoEditorEnabled && queryParams.mode === 'edit'
        ? 'editor'
        : 'read-only',
    isLoading,
    meta,
    repositoryAccessLevel: state.repository.details.accessLevel,
    userIsAnonymous: getCurrentUserIsAnonymous(state),
  };
};

type SourceFileProps = RouteComponentProps<{ refOrHash: string }> &
  ConnectedSourceFileProps & {
    atRef?: Ref;
    contents?: string;
    currentRef?: Ref;
    dispatch: Dispatch;
    isLoading: boolean;
    meta: FileMeta;
    repositoryAccessLevel?: RepositoryPrivilege;
    userIsAnonymous: boolean;
  };

type SourceFileState = {
  fileEditorMode: FileEditorMode;
};

class SourceFile extends PureComponent<SourceFileProps, SourceFileState> {
  state = {
    fileEditorMode: this.props.initialFileEditorMode,
  };

  componentDidMount() {
    this.loadSourceFile();
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  componentDidUpdate(prevProps) {
    if (this.props.path !== prevProps.path) {
      this.loadSourceFile();
    }
  }

  componentWillUnmount() {
    this.props.dispatch({ type: UNLOAD_FILE });
  }

  buildPathSegmentUrl = (path: string) => {
    const { atRef, match, repository } = this.props;
    return urls.ui.source(repository.full_name, {
      path,
      refOrHash: match.params.refOrHash,
      at: atRef && atRef.name,
    });
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  publishFileAnalyticEvent = (label, repoUUID, workspaceUUID) => {
    publishUiEvent({
      source: 'file',
      action: 'clicked',
      actionSubject: 'dropdown',
      actionSubjectId: 'SourceFileActionDropdown',
      objectType: 'repository',
      objectId: repoUUID,
      containerType: 'workspace',
      containerId: workspaceUUID,
      attributes: {
        label,
        repositoryUuid: repoUUID,
        frontbucketView: true,
      },
    });
  };

  loadSourceFile = () => {
    this.props.dispatch(loadSourceFile(this.props.path));
  };

  onCommitChanges = (path: string, fileContents: string) => {
    this.props.dispatch(
      initiatePendingEdit({
        fileContents,
        filePath: this.props.path,
        newFilePath: path,
      })
    );
  };

  onDelete = () => {
    const { repository } = this.props;
    this.publishFileAnalyticEvent(
      'delete',
      uncurlyUuid(repository.uuid),
      repository?.workspace ? uncurlyUuid(repository.workspace.uuid) : ''
    );
    this.props.dispatch(initiatePendingDelete(this.props.path));
  };

  onChangeFileEditorMode = (e: FileEditorModeChangeEvent) => {
    const { repository } = this.props;

    if (e.fileEditorMode === 'editor') {
      this.publishFileAnalyticEvent(
        'edit',
        uncurlyUuid(repository.uuid),
        repository?.workspace ? uncurlyUuid(repository.workspace.uuid) : ''
      );
    }

    if (this.props.isMonacoEditorEnabled) {
      this.setState({ fileEditorMode: e.fileEditorMode });
      /**
       * Only prevent the default behavior for clicks (this is meant to capture clicks on the "Edit" button)
       *
       * Toggles in the footer need to preserve their "change" event behavior in order to function correctly
       * (otherwise they behave oddly and require 2 clicks/keypresses to toggle)
       */
      if (e.event.type === 'click') {
        e.event.preventDefault();
      }
    }
  };

  onOpenRaw = () => {
    const { repository } = this.props;
    this.publishFileAnalyticEvent(
      'raw',
      uncurlyUuid(repository.uuid),
      repository?.workspace ? uncurlyUuid(repository.workspace.uuid) : ''
    );
  };

  onSelectConnectEditor = (editor?: string) => {
    const queryParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    if (editor === queryParams.editor) {
      return;
    }

    const { atRef, match, path, repository } = this.props;
    const url = urls.ui.source(repository.full_name, {
      at: atRef && atRef.name,
      connectEditor: editor,
      path,
      refOrHash: match.params.refOrHash,
    });
    this.props.history.push(url);
  };

  onSelectConnectViewer = (viewer?: string) => {
    const queryParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    if (viewer === queryParams.viewer) {
      return;
    }

    const { atRef, match, path, repository } = this.props;
    const url = urls.ui.source(repository.full_name, {
      at: atRef && atRef.name,
      connectViewer: viewer,
      path,
      refOrHash: match.params.refOrHash,
    });
    this.props.history.push(url);
  };

  onRename = () => {
    const { contents, repository } = this.props;
    // `contents` is an empty string for empty files, which can be renamed
    // `contents` is `undefined` if the file hasn't been loaded yet (it's
    // either pending, or it's a binary or LFS file that we don't fetch
    // the contents of at all)
    if (contents !== undefined) {
      this.publishFileAnalyticEvent(
        'rename',
        uncurlyUuid(repository.uuid),
        repository?.workspace ? uncurlyUuid(repository.workspace.uuid) : ''
      );
      this.props.dispatch(initiatePendingRename(this.props.path, contents));
    }
  };

  onToggleAnnotate = () => {
    const { atRef, currentRef, match, path, repository } = this.props;
    const { refOrHash } = match.params;
    this.publishFileAnalyticEvent(
      'blame',
      uncurlyUuid(repository.uuid),
      repository?.workspace ? uncurlyUuid(repository.workspace.uuid) : ''
    );
    if (refOrHash) {
      const annotateUrl = urls.ui.blame(repository.full_name, refOrHash, path, {
        at: atRef || currentRef,
        applyRouterFix: true,
      });
      this.props.history.push(annotateUrl);
    }
  };

  isAtHead = () => {
    const ref = this.props.atRef || this.props.currentRef;
    return ref && ref.target.hash === this.props.hash;
  };

  render() {
    const {
      atRef,
      currentRef,
      contents,
      hash,
      location,
      isLoading,
      meta,
      path,
      repository,
      repositoryAccessLevel,
      userIsAnonymous,
      match,
    } = this.props;

    const queryParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    const { editor: connectEditorModuleKey, viewer: connectViewerModuleKey } =
      queryParams;

    const { refOrHash } = match.params;

    return (
      <ContextualAnalyticsData sourceName="fileSource" sourceType={SCREEN}>
        <FireScreenAnalytics />

        <File
          at={atRef || currentRef}
          buildPathSegmentUrl={this.buildPathSegmentUrl}
          connectEditorModuleKey={connectEditorModuleKey as string | undefined}
          connectViewerModuleKey={connectViewerModuleKey as string | undefined}
          contents={contents}
          fileEditorMode={this.state.fileEditorMode}
          hash={hash}
          refOrHash={refOrHash}
          isAtHead={this.isAtHead()}
          isLoading={isLoading}
          meta={meta}
          onChangeFileEditorMode={this.onChangeFileEditorMode}
          onCommitChanges={this.onCommitChanges}
          onDelete={this.onDelete}
          onOpenRaw={this.onOpenRaw}
          onRename={this.onRename}
          onSelectConnectEditor={this.onSelectConnectEditor}
          onSelectConnectViewer={this.onSelectConnectViewer}
          onToggleAnnotate={this.onToggleAnnotate}
          path={path}
          repository={repository}
          repositoryAccessLevel={repositoryAccessLevel}
          userIsAnonymous={userIsAnonymous}
        />
        <CommitModalDialog repositoryFullSlug={repository.full_name} />
        {!!contents && !isLoading && <MetricSender metric={SOURCE_FILE_LOAD} />}
      </ContextualAnalyticsData>
    );
  }
}

export default withRouter(connect(mapStateToProps)(SourceFile));
